/* eslint no-nested-ternary:0 */
/* eslint react/no-array-index-key:0 */
import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import React, { useState, useEffect } from 'react'
import { Devices, useBrowser } from '@s4l/utils/hooks/useBrowser'
import { useS4LContext } from '@s4l/react-context'
import cn from 'classnames'
import debug from 'debug'

import styles from './styles.module.css'
import { Button, Card, MessageBox, Typography } from '@s4l/ds'
import { ListOfPaginatedProperty } from '@s4l/stayforlong/property/ListOfPaginatedProperty'
import PropertyCard from '../PropertyCard'
import type { UseCaseResponse } from '@s4l/stayforlong/kernel/architecture'

import { Image } from '../../index'

const log = debug('s4l:uikit:userLikedProperties')
const TEN_SECONDS = 10_000

export interface UserLikedPropertiesProps {
  paginatedUserLikedPropertiesJSON: ReturnType<ListOfPaginatedProperty['toJSON']> | null
}

function UserLikedProperties({
  paginatedUserLikedPropertiesJSON,
}: Readonly<UserLikedPropertiesProps>) {
  const [isLoading, setIsLoading] = useState(false)
  const [paginatedProperties, setPaginatedProperties] = useState(
    ListOfPaginatedProperty.fromJSONOrEmpty(paginatedUserLikedPropertiesJSON)
  )
  const [showError, setShowError] = useState(false)
  const [showNoHotelsLikedFeedback, setShowNoHotelsLikedFeedback] = useState(false)
  const { domain, i18n } = useS4LContext()
  const { device } = useBrowser()

  const itemPerLine = device === Devices.Mobile ? 1 :   // prettier-ignore
                      device === Devices.Tablet ? 2 :   // prettier-ignore
                                                  3 // prettier-ignore

  useEffect(() => {
    if (!paginatedUserLikedPropertiesJSON) {
      setShowError(true)
      return
    }

    if (paginatedProperties.isEmpty()) setShowNoHotelsLikedFeedback(true)
  }, [paginatedProperties, paginatedUserLikedPropertiesJSON])

  useEffect(() => {
    if (showError) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [showError])

  const hasMore = paginatedProperties.hasMorePages()

  const handleLoadMore = async () => {
    try {
      setIsLoading(true)
      setShowError(false)
      const nextPage = paginatedProperties.nextPage().page

      const timeoutPromise = new Promise(resolve => {
        setTimeout(() => resolve(new Error('Timeout')), TEN_SECONDS)
      })

      const loadPromise = domain.GetListOfLikesPropertyUseCase.execute({
        page: nextPage,
      })

      const result = (await Promise.race([loadPromise, timeoutPromise])) as
        | UseCaseResponse<ListOfPaginatedProperty>
        | Error

      if (result instanceof Error) {
        log('Timeout getting properties:', result)
        setShowError(true)
        return
      }

      const [error, newProperties] = result

      if (error || !newProperties) {
        log('No properties returned')
        setShowError(true)
        return
      }

      setPaginatedProperties(newProperties.shiftProperties({ properties: paginatedProperties }))
    } catch (error) {
      log('Unkown Error', error)
      setShowError(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleUnlikeProperty = async (propertyId: string) => {
    try {
      const propertyElement = document.querySelector(`[data-property-id="${propertyId}"]`)
      if (propertyElement) {
        propertyElement.classList.add(styles.removing)
      }

      const [error] = await domain.UnlikeUserPropertyUseCase.execute({ id: propertyId })

      if (error) {
        log('Error unliking property:', error)
        const propertyElement = document.querySelector(`[data-property-id="${propertyId}"]`)
        if (propertyElement) {
          propertyElement.classList.remove(styles.removing)
        }
        setShowError(true)
        return
      }

      const updatedProperties = paginatedProperties.properties.removeById(propertyId)
      const updatedPaginatedProperties = ListOfPaginatedProperty.create({
        paginationStatus: paginatedProperties.paginationStatus,
        properties: updatedProperties,
      })

      setPaginatedProperties(updatedPaginatedProperties)

      if (!updatedPaginatedProperties.hasProperties() && !hasMore) {
        setShowNoHotelsLikedFeedback(true)
      }
    } catch (error) {
      log('Error removing property:', error)

      const propertyElement = document.querySelector(`[data-property-id="${propertyId}"]`)
      if (propertyElement) {
        propertyElement.classList.remove(styles.removing)
      }

      setShowError(true)
    }
  }

  const ErrorPropertyMessageProps = {
    kind: 'error' as const,
    ...(paginatedUserLikedPropertiesJSON && { onClose: () => setShowError(false) }),
  }

  return (
    <>
      {showError && (
        <div className={styles.errorContainer}>
          <MessageBox {...ErrorPropertyMessageProps}>
            <MessageBox.Title>
              {i18n.formatMessage({ id: 'property-like.error-title' })}
            </MessageBox.Title>
            <MessageBox.Body>
              {i18n.formatMessage({ id: 'property-like.error-body' })}
            </MessageBox.Body>
          </MessageBox>
        </div>
      )}
      <div className={styles.propertyContainer}>
        <div className={styles.propertyGrid}>
          {
            paginatedProperties.map<React.ReactNode>(property => {
              return (
                <div
                  key={property.id}
                  className={styles.propertyCard}
                  data-property-id={property.id}
                >
                  <PropertyCard variant="fixed-height" propertyJSON={property.toJSON()}>
                    <PropertyCard.ActionLikeUnlike
                      isLiked
                      onClick={() => handleUnlikeProperty(property.id)}
                    />
                  </PropertyCard>
                </div>
              )
            }) as React.ReactNode[]
          }
        </div>

        {isLoading && (
          <div
            className={cn(styles.propertyGrid, styles.propertyPlaceholderGrid)}
            data-testid="card-placeholder"
          >
            {Array.from({ length: itemPerLine }).map((_, index) => (
              <Card.Placeholder variant="fixed-height" key={index} />
            ))}
          </div>
        )}

        {hasMore && !isLoading && (
          <div className={styles.buttonContainer}>
            <Button
              variant="outlined-rounded"
              size="large"
              onClick={handleLoadMore}
              disabled={isLoading}
            >
              {i18n.formatMessage({
                id: 'global.load-more',
              })}
            </Button>
          </div>
        )}

        {showNoHotelsLikedFeedback && (
          <div className={styles.noHotelsYetContainer}>
            <Image
              width={144}
              height={144}
              alt="No hotels yet"
              src={`${process.env.NEXT_PUBLIC_CLOUDFRONT_ENDPOINT}/images/illustrations/like.svg`}
            />
            <Typography as="p" variant="body" size="xl" bold>
              {i18n.formatMessage({ id: 'property-like.no-hotels-yet-title' })}
            </Typography>
            <Typography as="p" variant="body" size="l">
              {i18n.formatMessage({ id: 'property-like.no-hotels-yet-body' })}
            </Typography>
          </div>
        )}
      </div>
    </>
  )
}

UserLikedProperties.displayName = 'UserLikedProperties'
export default withErrorBoundary(UserLikedProperties)
